import React from "react";
import { Link } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import moment from "moment";
import trendingUpIcon from "../../../../../assets/icons/trending_up_icon.png";
import outboundIcon from "../../../../../assets/icons/outbound.png";
import "../styles/trendChartDialog.scss";

/**
 * TrendChartDialog - Reusable trend chart popup for clinical data
 *
 * Props:
 * - open: boolean
 * - onClose: function
 * - title: string (e.g. "Blood Pressure Trend")
 * - data: array of { date, value } objects
 * - unit: string (e.g. "mmHg")
 * - lastValue: string (e.g. "165/95 mmHg")
 * - status: string (e.g. "Elevated")
 * - statusClass: string (tag class)
 * - aiInsight: string (optional AI insight text)
 */
function TrendChartDialog({
  open,
  onClose,
  title,
  data = [],
  loading = false,
  unit = "",
  lastValue = "",
  status = "",
  statusClass = "",
  aiInsight = "",
  patientId,
}) {
  if (!open) return null;

  const formatXAxis = (dateStr) => {
    if (!dateStr) return "";
    return moment(dateStr).format("D MMM\nYYYY");
  };

  return (
    <div className="trend-chart-overlay" onClick={onClose}>
      <div className="trend-chart-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="trend-chart-header">
          <h3 className="trend-chart-title">{title}</h3>
          <button className="trend-chart-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="trend-chart-body">
          {loading ? (
            <div className="trend-chart-loading">Loading trend data...</div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart
                data={data}
                margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatXAxis}
                  tick={{ fontSize: 11, fill: "#6b7280" }}
                  axisLine={{ stroke: "#e5e7eb" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#6b7280" }}
                  axisLine={{ stroke: "#e5e7eb" }}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(value) => [`${value} ${unit}`, "Value"]}
                  labelFormatter={(label) =>
                    moment(label).format("DD MMM YYYY")
                  }
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#E44437"
                  strokeWidth={2}
                  dot={{
                    r: 5,
                    fill: "#fff",
                    stroke: "#E44437",
                    strokeWidth: 2,
                  }}
                  activeDot={{ r: 7, fill: "#E44437" }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="trend-chart-footer">
          <div className="trend-chart-info">
            <span className="trend-chart-last-value">
              <strong>Last updated value:</strong> {lastValue}
            </span>
            {/* The patient's HEALTH RECORD, /patient-record/:patientId — a
                router Link in the SAME tab, like every other patient link on
                bridge since 2026-09-25. It was a window.open of the legacy
                patients/new-bridge screen behind a span with role="button",
                which no amount of tabIndex makes middle-clickable or
                copyable. */}
            {patientId ? (
              <Link
                className="trend-chart-view-details"
                to={`/patient-record/${patientId}`}
              >
                View details
                <img src={outboundIcon} alt="" className="view-details-icon" />
              </Link>
            ) : null}
          </div>
          <div className="trend-chart-status">
            <strong>Status:</strong>{" "}
            <span className={`status-tag ${statusClass}`}>{status}</span>
          </div>
          {aiInsight && (
            <div className="trend-chart-ai-insight">
              <div className="ai-insight-header">
                <img
                  src={trendingUpIcon}
                  alt="trend"
                  className="ai-insight-icon"
                />
                <strong>AI Insight</strong>
              </div>
              <p className="ai-insight-text">{aiInsight}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TrendChartDialog;
